import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSearchParams, useNavigate } from "react-router-dom";
import logo from '@/assets/logo.webp';
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";

const ResetPassword = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { setUser } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const token = searchParams.get("token");

    if (!token) {
        navigate("/admin/login");
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // Check if passwords match
        if (newPassword !== confirmPassword) {
            toast({
                title: t("resetPassword.toastPasswordMismatchTitle"),
                description: t("resetPassword.toastPasswordMismatchDescription"),
                variant: "destructive",
                duration: 5000,
            });
            return; // Stop form submission
        }

        setIsSubmitting(true);

        try {
            // Make the API call to reset the password
            const response = await fetch(`${import.meta.env.VITE_ADMIN_RESET_PASSWORD_ENDPOINT}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ token, newPassword, confirmPassword }),
            });

            if (!response.ok) {
                throw new Error("Password reset failed");
            }

            // Parse the response body (assuming it's JSON)
            const data = await response.json();

            const user = jwtDecode(data.token, { header: false }) as {
                sub: string;
                given_name: string;
                family_name: string;
                email: string;
            };

            const adminUser = {
                id: user.sub,
                firstName: user.given_name,
                lastName: user.family_name,
                email: user.email,
            }

            const userData = {
                id: user.sub,
                email: adminUser.email,
                firstName: adminUser.firstName,
                lastName: adminUser.lastName,
                token: data.token,
            };

            window.localStorage.setItem('currentUser', JSON.stringify(adminUser));
            window.sessionStorage.setItem(user.sub, data.token);

            setUser(userData);

            // Redirect to the dashboard after successful login
            navigate("/admin/dashboard");
        } catch (error) {
            console.error("Password reset error:", error);
            toast({
                title: t("resetPassword.toastErrorTitle"),
                description: t("resetPassword.toastErrorDescription"),
                variant: "destructive", // Toast style for errors
                duration: 5000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        document.title = `${t('resetPassword.title')} - FitCalendar`;
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <Card className="w-full max-w-md">
                <div className="flex flex-col items-center">
                    <a href="/"><img src={logo} alt="FitCalendar" className="h-6 object-contain mt-8 mb-8" /></a>
                </div>
                <CardHeader>
                    <CardTitle>{t("resetPassword.title")}</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="password">{t("resetPassword.newPasswordLabel")}</Label>
                            <Input
                                id="newPassword"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">{t("resetPassword.confirmPasswordLabel")}</Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                        </div>
                        <Button type="submit" className="w-full" disabled={isSubmitting}>
                            {isSubmitting ? t("resetPassword.sendingButton") : t("resetPassword.sendResetLinkButton")}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default ResetPassword;
